import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { INVITE_FIELDS, teammateFieldCount } from '@gg/shared';
import { InviteForm } from '@/components/invitations';
import { TeammateForm } from '@/components/teams';
import { Button, buttonClasses, CheckIcon, MailIcon, Notice, StickyFooter } from '@/components/ui';
import { funnel } from '@/lib/analytics';
import { clearCheckout } from '@/lib/checkoutDraft';
import { formatDay, formatTime } from '@/lib/dates';
import { saveInviteDraft } from '@/lib/inviteDraft';
import { clearOrderClaim, readOrderClaim } from '@/lib/orderClaim';
import { localName } from '@/lib/formats';
import { orderOutcome, paymentTaken } from '@/lib/orderOutcome';
import { useClaimOrderSession, useMe, useOrder, useResendConfirmation } from '@/lib/queries';

/** PRD §4.1: poll for up to 30 s, then say the confirmation is on its way by email. */
const FAST_POLL_MS = 2_000;
const SLOW_POLL_MS = 10_000;
const FAST_POLL_FOR_MS = 30_000;

/**
 * Screen 04: confirmation + invite 5 friends (P-07). Shown on the client-side payment success;
 * the order turns `paid` only through the Stripe webhook, which this page polls for. Once paid,
 * the checkout's claim token opens the session the invitations need.
 * For a team order (P-20) the one action is to invite the teammates instead; friends can still
 * be invited from the profile.
 * A buyer the claim can't log in (existing account, claim missing or spent) is sent to the
 * confirmation email instead, whose links log them in (P-05, record 0026).
 */
export function Success() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const orderId = params.get('order') ?? undefined;
  // Set by Stripe on the return_url after every confirmation (cards, wallets, 3DS).
  const redirectStatus = params.get('redirect_status');
  const failed = redirectStatus === 'failed';
  const taken = paymentTaken(redirectStatus);

  // The order is paid or on its way: the next visit starts a fresh checkout.
  useEffect(() => {
    if (!failed) clearCheckout();
  }, [failed]);

  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSlow(true), FAST_POLL_FOR_MS);
    return () => clearTimeout(id);
  }, []);
  const { data: order } = useOrder(orderId, slow ? SLOW_POLL_MS : FAST_POLL_MS, taken);
  const me = useMe();
  const claim = useClaimOrderSession();
  const claimStarted = useRef(false);
  const [needLogin, setNeedLogin] = useState(false);
  // No session for this buyer: the screen stays as it is until they head for their profile, then
  // says to open the confirmation email (record 0027, R1). Counts the addresses kept, if any.
  const [leaving, setLeaving] = useState<number | null>(null);

  const signedIn = Boolean(me.data && order && me.data.email === order.email);
  const paid = order?.status === 'paid';

  useEffect(() => {
    if (!paid || !order || me.isLoading || signedIn || claimStarted.current) return;
    claimStarted.current = true;
    const token = readOrderClaim(order.orderId);
    if (!token) {
      setNeedLogin(true);
      return;
    }
    claim.mutate(
      { orderId: order.orderId, token },
      {
        onSuccess: () => clearOrderClaim(order.orderId),
        onError: () => setNeedLogin(true),
      },
    );
  }, [paid, order, me.isLoading, signedIn, claim]);

  const tracked = useRef(false);
  useEffect(() => {
    if (paid && order && !tracked.current) {
      tracked.current = true;
      funnel.purchase(order.orderId, order.totalCents);
    }
  }, [paid, order]);

  if (failed) return <Navigate to="/pay" replace state={{ failed: true }} />;
  if (!orderId) return <Navigate to="/formats" replace />;
  if (!order) return <p className="text-center text-ink-muted">…</p>;

  if (orderOutcome(order.status, taken) === 'failed') {
    return (
      <section className="flex flex-col gap-4 text-center">
        <p className="m-0">{t('confirmation.failed')}</p>
        <Link to="/formats" className={buttonClasses('primary')}>
          {t('confirmation.retry')}
        </Link>
      </section>
    );
  }

  const races = order.registrations
    .map((r) => {
      const name = localName(r.formatName, r.formatNameEn);
      return r.formatStartsAt
        ? `${name} · ${formatDay(r.formatStartsAt, 'long')}, ${formatTime(r.formatStartsAt)}`
        : name;
    })
    .join(', ');
  // Team order: shown as such from the start. A captain's team has no id until the webhook has
  // created it; the form stays the same (keeping what was typed) and its submit waits for both.
  const team = order.team;
  const seatsLeft = team ? (team.sizeMax === null ? null : team.sizeMax - team.memberCount) : 0;
  const teammateFields = team ? teammateFieldCount(seatsLeft) : 0;
  const waitingNote = !paid
    ? t(slow ? 'invite.waiting.paymentLong' : 'invite.waiting.payment')
    : t('invite.waiting.session');
  const noSession = needLogin && !signedIn;
  const later = noSession ? (
    <Button type="button" variant="ghost" size="md" onClick={() => setLeaving(0)}>
      {t('invite.later')}
    </Button>
  ) : (
    <Link to="/me" className={buttonClasses('ghost', 'md')}>
      {t('invite.later')}
    </Link>
  );
  const member = team?.role === 'member';
  // The addresses typed without a session wait in this browser for the email's link.
  const hold = (key: 'invite' | `team:${string}`) => (emails: string[]) => {
    saveInviteDraft(key, emails);
    setLeaving(emails.length);
  };
  const inbox = noSession && leaving !== null && (
    <CheckInbox
      orderId={order.orderId}
      email={order.email}
      body={
        member
          ? 'confirmation.inbox.bodyMember'
          : team
            ? 'confirmation.inbox.bodyTeam'
            : 'confirmation.inbox.body'
      }
      held={leaving}
    />
  );

  return (
    <section className="flex flex-1 flex-col">
      <div className="-mx-5 -mt-6 flex flex-col gap-2.5 bg-ink px-5 pt-8 pb-6 text-paper">
        <span className="flex size-11 items-center justify-center rounded-full bg-accent">
          <CheckIcon size={24} />
        </span>
        <h1 className="m-0 mt-1 font-display text-title font-bold">{t('confirmation.title')}</h1>
        <p className="m-0 text-intro text-ink-inverse-muted">
          {team
            ? t('confirmation.teamSummary', { races, team: team.name, email: order.email })
            : t('confirmation.summary', { races, email: order.email })}
          {!paid && slow && <> {t('confirmation.delayed')}</>}
        </p>
      </div>

      {team && member ? (
        // A teammate paid their place (P-21): nothing to invite, their profile has the team.
        <div className="flex flex-1 flex-col gap-4 pt-6">
          <div className="flex flex-col gap-1">
            <h2 className="m-0 font-display text-section-lg font-bold">
              {t('team.member.title', { name: team.name })}
            </h2>
            <p className="m-0 text-sm leading-snug text-ink-muted">{t('team.member.subtitle')}</p>
          </div>
          {inbox || (
            <StickyFooter>
              {signedIn ? (
                <Link to="/me" className={buttonClasses('primary')}>
                  {t('team.member.profile')}
                </Link>
              ) : noSession ? (
                <Button type="button" onClick={() => setLeaving(0)}>
                  {t('team.member.profile')}
                </Button>
              ) : (
                // The profile needs the session the claim opens once the order is paid: until
                // then the action waits, as the captain's "Send" does.
                <>
                  <Button type="button" disabled>
                    {t('team.member.profile')}
                  </Button>
                  <p role="status" className="m-0 text-center text-helper text-ink-muted">
                    {waitingNote}
                  </p>
                </>
              )}
            </StickyFooter>
          )}
        </div>
      ) : team ? (
        <div className="flex flex-1 flex-col gap-4 pt-6">
          <div className="flex flex-col gap-1">
            <h2 className="m-0 font-display text-section-lg font-bold">
              {t('team.afterPayment.title', { name: team.name })}
            </h2>
            <p className="m-0 text-sm leading-snug text-ink-muted">
              {t('team.afterPayment.subtitle')}
            </p>
          </div>
          {inbox || (
            <TeammateForm
              teamId={team.id ?? ''}
              fields={teammateFields}
              maxFields={seatsLeft}
              ready={signedIn && team.id !== null}
              waitingNote={paid ? t('invite.waiting.session') : waitingNote}
              secondaryAction={later}
              onHold={noSession && team.id ? hold(`team:${team.id}`) : undefined}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-4 pt-6">
          <div className="flex flex-col gap-1">
            <h2 className="m-0 font-display text-section-lg font-bold">
              {t('invite.afterPayment.title')}
            </h2>
            <p className="m-0 text-sm leading-snug text-ink-muted">
              {t('invite.afterPayment.subtitle')}
            </p>
          </div>

          {inbox || (
            <InviteForm
              fields={INVITE_FIELDS}
              ready={signedIn}
              waitingNote={waitingNote}
              secondaryAction={later}
              onHold={noSession ? hold('invite') : undefined}
            />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The order is paid, this browser can't open a session (P-05, record 0026) and the buyer heads
 * for their profile: the links in the confirmation email log them in. "Resend the email" needs
 * the checkout's claim token, so it only shows in the browser that paid.
 */
function CheckInbox({
  orderId,
  email,
  body,
  held,
}: {
  orderId: string;
  email: string;
  body: string;
  /** Addresses kept for the email's link to send. */
  held: number;
}) {
  const { t } = useTranslation();
  const resend = useResendConfirmation();
  const token = readOrderClaim(orderId);
  return (
    <>
      <Notice
        title={t('confirmation.inbox.title')}
        icon={<MailIcon size={22} className="text-accent" />}
      >
        <Trans
          i18nKey={body}
          values={{ email }}
          components={{ b: <span className="font-semibold text-ink" /> }}
        />
        {held > 0 && <> {t('confirmation.inbox.held')}</>}
      </Notice>
      {token && (
        <StickyFooter>
          <Button
            type="button"
            variant="secondary"
            loading={resend.isPending}
            disabled={resend.isSuccess}
            onClick={() => resend.mutate({ orderId, token })}
          >
            {t('confirmation.inbox.resend')}
          </Button>
          {resend.isSuccess && (
            <p role="status" className="m-0 text-center text-helper text-ink-muted">
              {t('confirmation.inbox.resent')}
            </p>
          )}
          {resend.isError && (
            <p role="alert" className="m-0 text-center text-helper text-danger">
              {t('error.generic')}
            </p>
          )}
        </StickyFooter>
      )}
    </>
  );
}
